import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useThemeStore } from '@/stores/theme'
import { useColors, formatCurrency } from '@/lib/utils'
import { AlertTriangle, CalendarRange, Filter, Info } from 'lucide-react'
import { useAuthStore } from '@/stores/auth'

export const Route = createFileRoute('/_authenticated/exceptions')({
  component: ExceptionsPage,
})

interface CompanyOption {
  id: string
  name: string
  apiValue: string
}

interface ExceptionEntry {
  id: string
  companyId: string
  companyName: string
  date: string
  storeNumber: string
  costCenter: string
  netSales: number
  department1: number
  department2: number
  department3: number
  department4: number
  credits: number
}

interface DraftEntry {
  date: string
  storeNumber: string
  costCenter: string
  netSales: string
  department1: string
  department2: string
  department3: string
  department4: string
  credits: string
}

const companyOptions: CompanyOption[] = [
  { id: 'churchs', name: 'Churchs', apiValue: 'churchs' },
  { id: 'nbrg', name: 'NBRG', apiValue: 'nbrg' },
  { id: 'piccadilly', name: 'Piccadilly', apiValue: 'piccadilly' },
  { id: 'ok', name: 'OK Holdings', apiValue: 'ok' },
  { id: 'kw', name: 'KW', apiValue: 'kw' },
]

const initialExceptions: ExceptionEntry[] = []

const formatDateInput = (date: Date) => date.toISOString().split('T')[0]

const getDefaultDateRange = () => {
  const today = new Date()
  const fourteenDaysAgo = new Date()
  fourteenDaysAgo.setDate(today.getDate() - 14)
  return {
    from: formatDateInput(fourteenDaysAgo),
    to: formatDateInput(today),
  }
}

const ensureTrailingSlash = (value: string) => (value.endsWith('/') ? value : `${value}/`)

const DEFAULT_EXCEPTIONS_API_BASE = 'https://nbrgreportazurefunctions-falcon.azurewebsites.net/api'

const EXCEPTIONS_API_BASE = ensureTrailingSlash(DEFAULT_EXCEPTIONS_API_BASE)
const EXCEPTIONS_UPDATE_BASE = EXCEPTIONS_API_BASE

function toDraft(entry: ExceptionEntry): DraftEntry {
  return {
    date: entry.date,
    storeNumber: entry.storeNumber,
    costCenter: entry.costCenter,
    netSales: entry.netSales.toString(),
    department1: entry.department1.toString(),
    department2: entry.department2.toString(),
    department3: entry.department3.toString(),
    department4: entry.department4.toString(),
    credits: entry.credits.toString(),
  }
}

function parseDraft(entry: DraftEntry): Partial<ExceptionEntry> {
  return {
    date: entry.date,
    storeNumber: entry.storeNumber,
    costCenter: entry.costCenter,
    netSales: Number.parseFloat(entry.netSales) || 0,
    department1: Number.parseFloat(entry.department1) || 0,
    department2: Number.parseFloat(entry.department2) || 0,
    department3: Number.parseFloat(entry.department3) || 0,
    department4: Number.parseFloat(entry.department4) || 0,
    credits: Number.parseFloat(entry.credits) || 0,
  }
}

const buildUpdatePayload = (entry: ExceptionEntry) => ({
  date: entry.date,
  storeNo: Number(entry.storeNumber),
  cc: Number(entry.costCenter),
  netSales: entry.netSales,
  dep1: entry.department1,
  dep2: entry.department2,
  dep3: entry.department3,
  dep4: entry.department4,
  credits: entry.credits,
})

function ExceptionsPage() {
  const { isDarkMode } = useThemeStore()
  const Colors = useColors(isDarkMode)
  const { userToken } = useAuthStore()
  const defaultRange = useMemo(() => getDefaultDateRange(), [])

  const [data, setData] = useState(initialExceptions)
  const [fromDate, setFromDate] = useState(defaultRange.from)
  const [toDate, setToDate] = useState(defaultRange.to)
  const [selectedCompany, setSelectedCompany] = useState<string>(companyOptions[0]?.id ?? '')
  const [selectedStore, setSelectedStore] = useState('')
  const [companyInputValue, setCompanyInputValue] = useState(companyOptions[0]?.name ?? '')
  const [storeInputValue, setStoreInputValue] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState<DraftEntry | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [totalRecords, setTotalRecords] = useState<number | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([])
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [savingId, setSavingId] = useState<string | null>(null)

  const headerCheckboxRef = useRef<HTMLInputElement>(null)
  const companyInputRef = useRef<HTMLInputElement>(null)
  const storeInputRef = useRef<HTMLInputElement>(null)
  const companyDropdownRef = useRef<HTMLDivElement>(null)
  const storeDropdownRef = useRef<HTMLDivElement>(null)
  const [isCompanyDropdownOpen, setIsCompanyDropdownOpen] = useState(false)
  const [isStoreDropdownOpen, setIsStoreDropdownOpen] = useState(false)

  const selectedCompanyOption = useMemo(
    () => companyOptions.find(option => option.id === selectedCompany) ?? null,
    [selectedCompany],
  )

  useEffect(() => {
    if (selectedCompanyOption) {
      setCompanyInputValue(selectedCompanyOption.name)
    }
  }, [selectedCompanyOption])

  useEffect(() => {
    setStoreInputValue(selectedStore)
  }, [selectedStore])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        companyDropdownRef.current &&
        !companyDropdownRef.current.contains(event.target as Node) &&
        companyInputRef.current &&
        !companyInputRef.current.contains(event.target as Node)
      ) {
        setIsCompanyDropdownOpen(false)
      }
      if (
        storeDropdownRef.current &&
        !storeDropdownRef.current.contains(event.target as Node) &&
        storeInputRef.current &&
        !storeInputRef.current.contains(event.target as Node)
      ) {
        setIsStoreDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (!userToken || !selectedCompanyOption) {
      setData([])
      setTotalRecords(null)
      setSelectedRowIds([])
      return
    }

    const controller = new AbortController()
    const storeFilter = selectedStore

    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        const cleanToken = userToken
          .toString()
          .trim()
          .replace(/^["']|["']$/g, '')
          .replace(/\s+/g, '')

        const url = new URL('atable-edit/list', EXCEPTIONS_API_BASE)
        url.searchParams.set('from', fromDate)
        url.searchParams.set('to', toDate)
        url.searchParams.set('company', selectedCompanyOption.apiValue)
        url.searchParams.set('page', page.toString())
        url.searchParams.set('pageSize', pageSize.toString())

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${cleanToken}`,
            'Accept': 'application/json',
          },
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Unable to load exceptions (status ${response.status})`)
        }

        const payload = await response.json()
        const items: any[] = Array.isArray(payload?.items) ? payload.items : []

        const mapped: ExceptionEntry[] = items.map((item, index) => {
          const dateValue = typeof item?.date === 'string' ? item.date : ''
          const normalizedDate = dateValue.includes('T') ? dateValue.split('T')[0] : dateValue
          const storeNo = item?.storeNo ?? item?.StoreNo ?? ''
          const cc = item?.cc ?? item?.CC ?? ''

          return {
            id: `${selectedCompanyOption.id}-${normalizedDate}-${storeNo}-${cc}-${index}`,
            companyId: selectedCompanyOption.id,
            companyName: selectedCompanyOption.name,
            date: normalizedDate,
            storeNumber: storeNo?.toString() ?? '',
            costCenter: cc?.toString() ?? '',
            netSales: Number(item?.netSales ?? item?.NetSales ?? 0),
            department1: Number(item?.dep1 ?? item?.Dep1 ?? 0),
            department2: Number(item?.dep2 ?? item?.Dep2 ?? 0),
            department3: Number(item?.dep3 ?? item?.Dep3 ?? 0),
            department4: Number(item?.dep4 ?? item?.Dep4 ?? 0),
            credits: Number(item?.credits ?? item?.Credits ?? 0),
          }
        })

        setData(mapped)
        setTotalRecords(typeof payload?.total === 'number' ? payload.total : mapped.length)
        setSelectedRowIds([])
        setEditingId(null)
        setDraft(null)

        if (storeFilter && mapped.every(entry => entry.storeNumber !== storeFilter)) {
          setSelectedStore('')
        }
      } catch (requestError) {
        if (controller.signal.aborted) {
          return
        }
        const message = requestError instanceof Error
          ? requestError.message
          : 'An unexpected error occurred while loading exceptions.'
        setError(message)
        setData([])
        setTotalRecords(null)
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      controller.abort()
    }
  }, [userToken, selectedCompanyOption, fromDate, toDate, page, pageSize])

  const filteredData = useMemo(() => {
    return data.filter(entry => {
      if (selectedCompany && entry.companyId !== selectedCompany) {
        return false
      }

      if (selectedStore && entry.storeNumber !== selectedStore) {
        return false
      }

      if (fromDate && entry.date < fromDate) {
        return false
      }

      if (toDate && entry.date > toDate) {
        return false
      }

      return true
    })
  }, [data, fromDate, selectedCompany, selectedStore, toDate])

  const availableStores = useMemo(() => {
    const unique = new Set(data.map(item => item.storeNumber))
    return Array.from(unique).sort()
  }, [data])

  const filteredCompanyOptions = useMemo(() => {
    const term = companyInputValue.trim().toLowerCase()
    if (!term) return companyOptions
    return companyOptions.filter(option => option.name.toLowerCase().includes(term))
  }, [companyInputValue])

  const filteredStores = useMemo(() => {
    const term = storeInputValue.trim().toLowerCase()
    if (!term) return availableStores
    return availableStores.filter(store => store.toLowerCase().includes(term))
  }, [storeInputValue, availableStores])

  const handleEdit = (entry: ExceptionEntry) => {
    setEditingId(entry.id)
    setDraft(toDraft(entry))
  }

  const handleDraftChange = (field: keyof DraftEntry, value: string) => {
    setDraft(prev => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleCancel = () => {
    setEditingId(null)
    setDraft(null)
  }

  const handleSave = async () => {
    if (!editingId || !draft || !selectedCompanyOption) {
      return
    }

    const existing = data.find(entry => entry.id === editingId)
    if (!existing) {
      return
    }

    const merged: ExceptionEntry = {
      ...existing,
      ...parseDraft(draft),
    }

    try {
      if (!userToken) {
        throw new Error('Authentication token is missing.')
      }

      setSavingId(editingId)

      const cleanToken = userToken
        .toString()
        .trim()
        .replace(/^["']|["']$/g, '')
        .replace(/\s+/g, '')

      const updateUrl = new URL(
        `atable-edit/${selectedCompanyOption.apiValue}/update`,
        EXCEPTIONS_UPDATE_BASE,
      )

      const response = await fetch(updateUrl.toString(), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(buildUpdatePayload(merged)),
      })

      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || 'Failed to update record.')
      }

      setData(prev =>
        prev.map(entry => (entry.id === editingId ? merged : entry))
      )

      setEditingId(null)
      setDraft(null)
    } catch (updateError) {
      const message =
        updateError instanceof Error
          ? updateError.message
          : 'An unexpected error occurred while updating the record.'
      window.alert(message)
    } finally {
      setSavingId(null)
    }
  }

  const toggleRowSelection = (id: string) => {
    setSelectedRowIds(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    )
  }

  const visibleRowIds = useMemo(() => filteredData.map(entry => entry.id), [filteredData])
  const allVisibleSelected = visibleRowIds.length > 0 && visibleRowIds.every(id => selectedRowIds.includes(id))
  const someVisibleSelected = visibleRowIds.some(id => selectedRowIds.includes(id))

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedRowIds(prev => prev.filter(id => !visibleRowIds.includes(id)))
    } else {
      setSelectedRowIds(prev => Array.from(new Set([...prev, ...visibleRowIds])))
    }
  }

  useEffect(() => {
    if (!headerCheckboxRef.current) return
    headerCheckboxRef.current.checked = allVisibleSelected
    headerCheckboxRef.current.indeterminate = !allVisibleSelected && someVisibleSelected
  }, [allVisibleSelected, someVisibleSelected])

  const handleBulkUpdate = async () => {
    if (!selectedCompanyOption) {
      window.alert('Please choose a company to update exceptions.')
      return
    }

    const rowsToUpdate = data.filter(entry => selectedRowIds.includes(entry.id))
    if (rowsToUpdate.length === 0) {
      return
    }

    if (!userToken) {
      window.alert('Authentication token is missing.')
      return
    }

    try {
      setBulkUpdating(true)

      const cleanToken = userToken
        .toString()
        .trim()
        .replace(/^["']|["']$/g, '')
        .replace(/\s+/g, '')

      const bulkUrl = new URL(
        `atable-edit/${selectedCompanyOption.apiValue}/bulk`,
        EXCEPTIONS_UPDATE_BASE,
      )

      const response = await fetch(bulkUrl.toString(), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${cleanToken}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(rowsToUpdate.map(buildUpdatePayload)),
      })

      if (!response.ok) {
        const message = await response.text()
        throw new Error(message || 'Failed to update selected records.')
      }

      window.alert('Selected records were updated successfully.')
      setSelectedRowIds([])
    } catch (bulkError) {
      const message =
        bulkError instanceof Error
          ? bulkError.message
          : 'An unexpected error occurred while updating records.'
      window.alert(message)
    } finally {
      setBulkUpdating(false)
    }
  }

  const totalPages = useMemo(() => {
    if (totalRecords === null) {
      return 1
    }
    return Math.max(1, Math.ceil(totalRecords / pageSize))
  }, [totalRecords, pageSize])

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages)
    }
  }, [page, totalPages])

  const canGoPrevious = page > 1
  const canGoNext = page < totalPages

  return (
    <div className="flex-1">
      <div
        className="sticky top-0 z-40 border-b px-6 py-3"
        style={{
          backgroundColor: Colors.background.paper,
          borderBottomColor: Colors.border.main,
        }}
      >
        <div className="flex items-center space-x-3">
          <AlertTriangle size={24} color={Colors.primary.main} />
          <div>
            <h1 className="text-xl font-inter-semibold" style={{ color: Colors.text.primary }}>
              Exceptions
            </h1>
            <p className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
              Review and adjust exception entries before final submission
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div
          className="p-6 rounded-xl"
          style={{
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode
              ? '0 2px 4px rgba(0, 0, 0, 0.2)'
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-2">
              <Filter size={18} color={Colors.primary.main} />
              <div>
                <h2 className="text-lg font-inter-semibold" style={{ color: Colors.text.primary }}>
                  Filter Exceptions
                </h2>
              </div>
            </div>
            <div
              className="flex items-center space-x-2 px-3 py-2 rounded-md text-xs font-inter-medium"
              style={{
                backgroundColor: Colors.neutral[200],
                color: Colors.text.secondary,
              }}
            >
              <CalendarRange size={14} />
              <span>
                {fromDate || 'Select start'} – {toDate || 'Select end'}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-4">
            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                From
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                To
              </label>
              <input
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(event) => {
                  setToDate(event.target.value)
                  setPage(1)
                }}
                className="w-full px-3 py-2 border rounded-md font-inter-regular"
                style={{
                  borderColor: Colors.border.main,
                  backgroundColor: Colors.background.default,
                  color: Colors.text.primary,
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                Company
              </label>
              <div className="relative">
                <div className="flex space-x-2">
                  <input
                    ref={companyInputRef}
                    type="text"
                    value={companyInputValue}
                    onFocus={() => setIsCompanyDropdownOpen(true)}
                    onChange={(event) => {
                      const value = event.target.value
                      setCompanyInputValue(value)
                      setIsCompanyDropdownOpen(true)
                    }}
                    placeholder="Select company"
                    className="flex-1 px-3 py-2 border rounded-md font-inter-regular"
                    style={{
                      borderColor: Colors.border.main,
                      backgroundColor: Colors.background.default,
                      color: Colors.text.primary,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const defaultOption = companyOptions[0]
                      if (defaultOption) {
                        setSelectedCompany(defaultOption.id)
                        setCompanyInputValue(defaultOption.name)
                      } else {
                        setSelectedCompany('')
                        setCompanyInputValue('')
                      }
                      setSelectedStore('')
                      setPage(1)
                      setIsCompanyDropdownOpen(false)
                    }}
                    className="px-3 py-2 rounded-md text-sm font-inter-medium"
                    style={{
                      backgroundColor: Colors.neutral[200],
                      color: Colors.text.secondary,
                    }}
                  >
                    Reset
                  </button>
                </div>
                {isCompanyDropdownOpen && (
                  <div
                    ref={companyDropdownRef}
                    className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border"
                    style={{
                      borderColor: Colors.border.main,
                      backgroundColor: Colors.background.paper,
                    }}
                  >
                    {filteredCompanyOptions.map(option => (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => {
                          setSelectedCompany(option.id)
                          setCompanyInputValue(option.name)
                          setSelectedStore('')
                          setPage(1)
                          setIsCompanyDropdownOpen(false)
                        }}
                        className="block w-full px-3 py-2 text-left text-sm font-inter-regular hover:bg-opacity-80"
                        style={{
                          backgroundColor:
                            selectedCompany === option.id ? Colors.neutral[200] : 'transparent',
                          color: Colors.text.primary,
                        }}
                      >
                        {option.name}
                      </button>
                    ))}
                    {filteredCompanyOptions.length === 0 && (
                      <div
                        className="px-3 py-2 text-sm font-inter-regular"
                        style={{ color: Colors.text.secondary }}
                      >
                        No companies found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-inter-medium mb-2" style={{ color: Colors.text.primary }}>
                Store
              </label>
              <div className="relative">
                <div className="flex space-x-2">
                  <input
                    ref={storeInputRef}
                    type="text"
                    value={storeInputValue}
                    onFocus={() => setIsStoreDropdownOpen(true)}
                    onChange={(event) => {
                      const value = event.target.value
                      setStoreInputValue(value)
                      setIsStoreDropdownOpen(true)
                    }}
                    placeholder="All stores"
                    className="flex-1 px-3 py-2 border rounded-md font-inter-regular"
                    style={{
                      borderColor: Colors.border.main,
                      backgroundColor: Colors.background.default,
                      color: Colors.text.primary,
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedStore('')
                      setStoreInputValue('')
                      setPage(1)
                      setIsStoreDropdownOpen(false)
                    }}
                    className="px-3 py-2 rounded-md text-sm font-inter-medium"
                    style={{
                      backgroundColor: Colors.neutral[200],
                      color: Colors.text.secondary,
                    }}
                  >
                    Clear
                  </button>
                </div>
                {isStoreDropdownOpen && (
                  <div
                    ref={storeDropdownRef}
                    className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border"
                    style={{
                      borderColor: Colors.border.main,
                      backgroundColor: Colors.background.paper,
                    }}
                  >
                    {filteredStores.map(store => (
                      <button
                        key={store}
                        type="button"
                        onClick={() => {
                          setSelectedStore(store)
                          setStoreInputValue(store)
                          setPage(1)
                          setIsStoreDropdownOpen(false)
                        }}
                        className="block w-full px-3 py-2 text-left text-sm font-inter-regular hover:bg-opacity-80"
                        style={{
                          backgroundColor:
                            selectedStore === store ? Colors.neutral[200] : 'transparent',
                          color: Colors.text.primary,
                        }}
                      >
                        {store}
                      </button>
                    ))}
                    {filteredStores.length === 0 && (
                      <div
                        className="px-3 py-2 text-sm font-inter-regular"
                        style={{ color: Colors.text.secondary }}
                      >
                        No stores found
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div
          className="p-6 rounded-xl"
          style={{
            backgroundColor: Colors.background.paper,
            boxShadow: isDarkMode
              ? '0 2px 4px rgba(0, 0, 0, 0.2)'
              : '0 2px 4px rgba(0, 0, 0, 0.05)',
          }}
        >
          {error && (
            <div
              className="mb-4 px-3 py-2 rounded-md text-sm font-inter-medium"
              style={{
                backgroundColor: Colors.error.main + '20',
                color: Colors.error.main,
              }}
            >
              {error}
            </div>
          )}

          {loading && (
            <div
              className="mb-4 px-3 py-2 rounded-md text-sm font-inter-medium flex items-center space-x-2"
              style={{
                backgroundColor: Colors.neutral[200],
                color: Colors.text.secondary,
              }}
            >
              <div
                className="h-3 w-3 rounded-full border-2 border-t-transparent animate-spin"
                style={{
                  borderColor: Colors.primary.main,
                  borderTopColor: 'transparent',
                }}
              ></div>
              <span>Loading exceptions…</span>
            </div>
          )}

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Info size={18} color={Colors.primary.main} />
              <p className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Select rows to update in bulk or edit a single record inline.
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <span
                className="text-xs font-inter-medium px-3 py-1 rounded-full"
                style={{
                  backgroundColor: Colors.neutral[200],
                  color: Colors.text.secondary,
                }}
              >
                {totalRecords !== null
                  ? `${filteredData.length} of ${totalRecords} records`
                  : `${filteredData.length} records`}
              </span>
              <button
                type="button"
                onClick={handleBulkUpdate}
                disabled={bulkUpdating || selectedRowIds.length === 0 || loading}
                className="px-4 py-2 rounded-md text-sm font-inter-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: Colors.primary.main }}
              >
                {bulkUpdating ? 'Updating…' : 'Done'}
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="border-b"
                  style={{
                    backgroundColor: isDarkMode ? Colors.background.paper : Colors.neutral[100],
                    borderBottomColor: Colors.border.main,
                  }}
                >
                  <th className="p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      onChange={toggleSelectAll}
                      className="h-4 w-4"
                    />
                  </th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Action</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Date</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Store</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>CC</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Net Sales</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Dep1</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Dep2</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Dep3</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Dep4</th>
                  <th className="text-left p-3 font-inter-semibold" style={{ color: Colors.text.primary }}>Credits</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map(entry => {
                  const isEditing = editingId === entry.id && draft

                  if (isEditing && draft) {
                    return (
                      <tr
                        key={entry.id}
                        className="border-b"
                        style={{ borderBottomColor: Colors.border.main }}
                      >
                        <td className="p-3 align-top">
                          <input
                            type="checkbox"
                            checked={selectedRowIds.includes(entry.id)}
                            onChange={() => toggleRowSelection(entry.id)}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="p-3 space-y-2">
                          <button
                            type="button"
                            onClick={handleSave}
                            disabled={savingId === entry.id}
                            className="block w-full px-3 py-2 rounded-md text-xs font-inter-semibold text-white disabled:opacity-50"
                            style={{ backgroundColor: Colors.primary.main }}
                          >
                            {savingId === entry.id ? 'Updating…' : 'Update'}
                          </button>
                          <button
                            type="button"
                            onClick={handleCancel}
                            disabled={savingId === entry.id}
                            className="block w-full px-3 py-2 rounded-md text-xs font-inter-semibold disabled:opacity-50"
                            style={{
                              backgroundColor: Colors.background.default,
                              color: Colors.text.secondary,
                              border: `1px solid ${Colors.border.main}`,
                            }}
                          >
                            Cancel
                          </button>
                        </td>
                        <td className="p-3">
                          <input
                            type="date"
                            value={draft.date}
                            onChange={(event) => handleDraftChange('date', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={draft.storeNumber}
                            onChange={(event) => handleDraftChange('storeNumber', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="text"
                            value={draft.costCenter}
                            onChange={(event) => handleDraftChange('costCenter', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.netSales}
                            onChange={(event) => handleDraftChange('netSales', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.department1}
                            onChange={(event) => handleDraftChange('department1', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.department2}
                            onChange={(event) => handleDraftChange('department2', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.department3}
                            onChange={(event) => handleDraftChange('department3', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.department4}
                            onChange={(event) => handleDraftChange('department4', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="number"
                            value={draft.credits}
                            onChange={(event) => handleDraftChange('credits', event.target.value)}
                            className="w-full px-3 py-2 border rounded-md font-inter-regular"
                            style={{
                              borderColor: Colors.border.main,
                              backgroundColor: Colors.background.default,
                              color: Colors.text.primary,
                            }}
                          />
                        </td>
                      </tr>
                    )
                  }

                  return (
                    <tr
                      key={entry.id}
                      className="border-b hover:bg-opacity-50"
                      style={{ borderBottomColor: Colors.border.main }}
                    >
                      <td className="p-3 align-top">
                        <input
                          type="checkbox"
                          checked={selectedRowIds.includes(entry.id)}
                          onChange={() => toggleRowSelection(entry.id)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => handleEdit(entry)}
                          className="text-xs font-inter-semibold px-3 py-2 rounded-md"
                          style={{
                            backgroundColor: Colors.background.default,
                            color: Colors.primary.main,
                            border: `1px solid ${Colors.primary.main}`,
                          }}
                        >
                          Edit
                        </button>
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {entry.date}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {entry.storeNumber}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {entry.costCenter}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.netSales)}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.department1)}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.department2)}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.department3)}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.department4)}
                      </td>
                      <td className="p-3 font-inter-regular" style={{ color: Colors.text.primary }}>
                        {formatCurrency(entry.credits)}
                      </td>
                    </tr>
                  )
                })}

                {filteredData.length === 0 && (
                  <tr>
                    <td colSpan={10} className="p-6 text-center font-inter-regular"
                      style={{ color: Colors.text.secondary }}
                    >
                      No exception records match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={() => canGoPrevious && setPage(prev => Math.max(1, prev - 1))}
                disabled={!canGoPrevious || loading}
                className="px-3 py-2 rounded-md text-sm font-inter-medium disabled:opacity-50"
                style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage(prev => prev + 1)}
                disabled={!canGoNext || loading}
                className="px-3 py-2 rounded-md text-sm font-inter-medium disabled:opacity-50"
                style={{ backgroundColor: Colors.neutral[200], color: Colors.text.primary }}
              >
                Next
              </button>
              <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Page {page} of {totalPages}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-inter-regular" style={{ color: Colors.text.secondary }}>
                Rows per page
              </span>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={(event) => {
                    const value = Number(event.target.value)
                    setPageSize(value)
                    setPage(1)
                  }}
                  className="appearance-none px-3 py-2 pr-8 border rounded-md font-inter-regular"
                  style={{
                    borderColor: Colors.border.main,
                    backgroundColor: Colors.background.default,
                    color: Colors.text.primary,
                  }}
                >
                  {[10, 25, 50, 100].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <div
                  className="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                  style={{ color: Colors.text.secondary }}
                >
                  ▼
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExceptionsPage
